import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  CircularProgress,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Card,
  Stack,
  Divider,
  useMediaQuery,
  useTheme,
  Tabs,
  Tab,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useDispatch, useSelector } from "react-redux";
import { enqueueSnackbar } from "notistack";
import ExchangeTag from "../Common/ExchangeTag";
import {
  fetchGTTOrdersAction,
  cancelGTTOrderAction,
  openGTTModal,
} from "../../store/actions/orders";
import CommonMessages from "../Common/NoDataMessages";
import NoOrdersIllustration from "../Common/NoOrdersIllustration";

const statusColor = (status) => {
  const s = (status || "").toLowerCase();
  if (s.includes("trigger") || s.includes("complete")) return "success";
  if (s.includes("cancel") || s.includes("reject") || s.includes("expire"))
    return "default";
  return "warning"; // pending / active
};

const num = (v) => (v === undefined || v === null || v === "" ? "-" : v);

const GTT = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const dispatch = useDispatch();

  const gttOrders = useSelector((state) => state.orders.gttOrdersData);
  const [loading, setLoading] = useState(true);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const [tabValue, setTabValue] = useState(0);

  const filteredOrders = useMemo(() => {
    if (!gttOrders) return [];
    return gttOrders.filter((order) => {
      const status = (order.status || "Active").toLowerCase();
      return tabValue === 0 ? status === "active" : status === "triggered";
    });
  }, [gttOrders, tabValue]);

  useEffect(() => {
    dispatch(fetchGTTOrdersAction());
    const t = setTimeout(() => setLoading(false), 800);
    return () => clearTimeout(t);
  }, [dispatch]);

  useEffect(() => {
    if (gttOrders) setLoading(false);
  }, [gttOrders]);

  const handleModify = (order) => {
    dispatch(
      openGTTModal({
        type: order.order_side,
        token: order.instrument_token,
        exchange: order.exchange,
        trading_symbol: order.trading_symbol,
        product: order.product,
        is_modify: true,
        gttData: order,
      })
    );
  };

  const handleCancelConfirm = async () => {
    if (!cancelTarget) return;
    setCancelling(true);
    const res = await dispatch(cancelGTTOrderAction(cancelTarget.id));
    setCancelling(false);
    setCancelTarget(null);
    enqueueSnackbar(
      res?.message ||
        (res?.status === "success"
          ? "GTT order cancelled"
          : "Failed to cancel GTT order"),
      { variant: res?.status === "success" ? "success" : "error" }
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
        <CircularProgress color="primary" />
      </Box>
    );
  }

  if (!gttOrders || gttOrders.length === 0) {
    return (
      <CommonMessages
        icon={<NoOrdersIllustration />}
        messageHead="No GTT orders"
        messageBody="Create a GTT order from the order window"
      />
    );
  }

  const renderActions = (order) => (
    <Stack direction="row" spacing={1} justifyContent="flex-end">
      <IconButton
        size="small"
        color="primary"
        onClick={() => handleModify(order)}
        aria-label="modify"
      >
        <EditIcon fontSize="small" />
      </IconButton>
      <IconButton
        size="small"
        color="error"
        onClick={() => setCancelTarget(order)}
        aria-label="cancel"
      >
        <DeleteOutlineIcon fontSize="small" />
      </IconButton>
    </Stack>
  );

  const cancelDialog = (
    <Dialog open={Boolean(cancelTarget)} onClose={() => setCancelTarget(null)}>
      <DialogTitle>Cancel GTT order?</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Are you sure you want to cancel the GTT order for{" "}
          <strong>
            {cancelTarget?.trading_symbol || cancelTarget?.instrument_token}
          </strong>
          ?
          <br />
          This action cannot be undone.
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setCancelTarget(null)} disabled={cancelling}>
          Keep
        </Button>
        <Button
          onClick={handleCancelConfirm}
          color="error"
          variant="contained"
          disabled={cancelling}
        >
          {cancelling ? "Cancelling..." : "Cancel GTT"}
        </Button>
      </DialogActions>
    </Dialog>
  );

  if (isMobile) {
    return (
      <>
        <Tabs
          value={tabValue}
          onChange={(e, newValue) => setTabValue(newValue)}
          variant="fullWidth"
          sx={{
            borderBottom: 1,
            borderColor: "divider",
            mb: 1,
            position: "sticky",
            top: '-1px',
            mt: '-1px',
            zIndex: 10,
            bgcolor: "background.paper",
          }}
        >
          <Tab label="Active" />
          <Tab label="Triggered" />
        </Tabs>
        <Stack spacing={1} sx={{ p: 1 }}>
          {filteredOrders.length === 0 ? (
            <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 3 }}>
              No {tabValue === 0 ? "active" : "triggered"} orders
            </Typography>
          ) : (
            filteredOrders.map((order) => (
            <Card key={order.id} sx={{ p: 1.5 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 1,
                }}
              >
                <Box>
                  <Typography variant="subtitle2" fontWeight={600}>
                    {order.trading_symbol || order.instrument_token}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                    <ExchangeTag exchange={order.exchange} />
                    <Typography variant="caption" color="text.secondary">
                      · {order.action_type === "oco" ? "OCO" : "Single"} ·{" "}
                      {order.product}
                    </Typography>
                  </Box>
                </Box>
                <Chip
                  size="small"
                  label={order.order_side}
                  color={order.order_side === "BUY" ? "success" : "error"}
                  variant="outlined"
                />
              </Box>
              <Divider sx={{ my: 1 }} />
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                  gap: 1,
                }}
              >
                <Typography variant="caption">Qty: {num(order.quantity)}</Typography>
                <Typography variant="caption">
                  Trigger: {num(order.trigger_price)}
                </Typography>
                <Typography variant="caption">Price: {num(order.price)}</Typography>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mt: 1,
                }}
              >
                <Chip
                  size="small"
                  label={order.status || "Active"}
                  color={statusColor(order.status)}
                />
                {renderActions(order)}
              </Box>
            </Card>
          )))}
        </Stack>
        {cancelDialog}
      </>
    );
  }

  return (
    <>
      <Tabs
        value={tabValue}
        onChange={(e, newValue) => setTabValue(newValue)}
        sx={{
          borderBottom: 1,
          borderColor: "divider",
          position: "sticky",
          top: '-1px',
          mt: '-1px',
          zIndex: 10,
          bgcolor: "background.paper",
        }}
      >
        <Tab label="Active" />
        <Tab label="Triggered" />
      </Tabs>
      <TableContainer
        component={Paper}
        sx={{
          height: '100%',
          maxHeight: 'none',
          boxShadow: 'none',
          backgroundColor: theme.palette.background.default,
          overflow: 'visible',
          paddingBottom: '24px',
          '& .MuiTableCell-root': {
            px: isMobile ? 1 : 1.5,
            py: isMobile ? 0.75 : 1,
            fontSize: isMobile ? '0.7rem' : '0.875rem',
            backgroundColor: theme.palette.background.paper,
            color: theme.palette.text.primary,
            borderBottom: `1px solid ${theme.palette.divider}`,
            padding: '12px 16px',
            height: 'auto',
            verticalAlign: 'middle'
          },
          '& .MuiTableHead-root .MuiTableCell-root': {
            top: '48px',
            zIndex: 9,
          },
          '&::after': {
            content: '""',
            display: 'block',
            height: '30px',
          }
        }}
      >
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Instrument</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Side</TableCell>
              <TableCell align="right">Qty</TableCell>
              <TableCell align="right">Trigger</TableCell>
              <TableCell align="right">Price</TableCell>
              <TableCell>Product</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredOrders.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                  No {tabValue === 0 ? "active" : "triggered"} orders
                </TableCell>
              </TableRow>
            ) : (
              filteredOrders.map((order) => (
                <TableRow key={order.id} hover>
                <TableCell>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                    <Typography variant="body2" fontWeight={600}>
                      {order.trading_symbol || order.instrument_token}
                    </Typography>
                    <Box sx={{ mt: 0.5 }}>
                      <ExchangeTag exchange={order.exchange} />
                    </Box>
                  </Box>
                </TableCell>
                <TableCell>
                  {order.action_type === "oco" ? "OCO" : "Single"}
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={order.order_side}
                    color={order.order_side === "BUY" ? "success" : "error"}
                    variant="outlined"
                  />
                </TableCell>
                <TableCell align="right">{num(order.quantity)}</TableCell>
                <TableCell align="right">{num(order.trigger_price)}</TableCell>
                <TableCell align="right">{num(order.price)}</TableCell>
                <TableCell>{order.product}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={order.status || "Active"}
                    color={statusColor(order.status)}
                  />
                </TableCell>
                <TableCell align="right">{renderActions(order)}</TableCell>
              </TableRow>
            )))}
          </TableBody>
        </Table>
      </TableContainer>
      {cancelDialog}
    </>
  );
};

export default GTT;
